<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="displayCategories"
      :loading="loading"
      :item-class="getItemClass"
      item-key="id"
      no-data-text="Không có đầu mục nào"
      :footer-props="{
        'items-per-page-text': 'Hiển thị mỗi trang',
        'items-per-page-all-text': 'Tất cả',
        'items-per-page-options': categories.length > 100 ? [10, 20, 50] : [10, 20, -1]
      }"
      disable-sort
    >
      <!-- Cột 1: Tên đầu mục hiển thị cây đa cấp -->
      <template v-slot:item.name="{ item }">
        <div
          class="d-flex align-center category-name-cell"
          :style="{ paddingLeft: item.isChildInTree ? (item.treeDepth - 1) * 14 + 'px' : '0px' }"
        >
          <!-- Đường kẻ phân nhánh ngắn cho đầu mục con -->
          <span v-if="item.isChildInTree" class="tree-branch-line"></span>

          <!-- Icon thư mục xanh -->
          <v-icon small color="primary" class="mr-2">mdi-folder</v-icon>

          <!-- Tên đầu mục -->
          <router-link
            :to="'/facility/assets?category=' + item.id"
            class="text-decoration-none mr-2 primary--text"
            title="Xem các thiết bị thuộc đầu mục này"
          >
            {{ item.name }}
          </router-link>
        </div>
      </template>

      <!-- Cột 2: Mã đầu mục -->
      <template v-slot:item.code="{ item }">
        <span>{{ item.code }}</span>
      </template>

      <!-- Cột 3: Đơn vị tính (Hiển thị tối đa 2 thẻ, từ 3 thẻ trở lên hiển thị 2 thẻ và +N) -->
      <template v-slot:item.units="{ item }">
        <div v-if="getItemUnits(item).length" class="d-flex flex-nowrap align-center">
          <v-tooltip v-for="(unit, idx) in getItemUnits(item).slice(0, 2)" :key="idx" top>
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                v-bind="attrs"
                v-on="on"
                small
                outlined
                color="primary"
                class="my-1 mr-1"
                style="cursor: pointer;"
              >
                {{ unit }}
              </v-chip>
            </template>
            <span>{{ unit }}</span>
          </v-tooltip>

          <!-- Thẻ +N khi nhiều hơn 2 đơn vị -->
          <v-tooltip v-if="getItemUnits(item).length > 2" top>
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                v-bind="attrs"
                v-on="on"
                small
                outlined
                color="primary"
                class="my-1 mr-1"
                style="cursor: pointer;"
              >
                +{{ getItemUnits(item).length - 2 }}
              </v-chip>
            </template>
            <span>{{ getItemUnits(item).slice(2).join(', ') }}</span>
          </v-tooltip>
        </div>
        <span v-else class="grey--text">-</span>
      </template>

      <!-- Cột 4: Tags dạng Chip màu sắc (Hiển thị tối đa 2 thẻ, từ 3 thẻ trở lên hiển thị 2 thẻ và +N) -->
      <template v-slot:item.tags="{ item }">
        <div v-if="getItemTags(item).length" class="d-flex flex-nowrap align-center">
          <v-tooltip v-for="(tag, idx) in getItemTags(item).slice(0, 2)" :key="idx" top>
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                v-bind="attrs"
                v-on="on"
                small
                :color="getTagColor(tag)"
                dark
                class="my-1 mr-1"
                style="cursor: pointer;"
              >
                {{ tag }}
              </v-chip>
            </template>
            <span>{{ getTagFullName(tag) }}</span>
          </v-tooltip>

          <!-- Thẻ +N khi nhiều hơn 2 thẻ -->
          <v-tooltip v-if="getItemTags(item).length > 2" top>
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                v-bind="attrs"
                v-on="on"
                small
                color="primary"
                dark
                class="my-1 mr-1"
                style="cursor: pointer;"
              >
                +{{ getItemTags(item).length - 2 }}
              </v-chip>
            </template>
            <span>{{ getItemTags(item).slice(2).map(getTagFullName).join(', ') }}</span>
          </v-tooltip>
        </div>
        <span v-else class="grey--text">-</span>
      </template>

      <!-- Cột 5: Ghi chú riêng biệt -->
      <template v-slot:item.description="{ item }">
        <span>{{ item.description || '-' }}</span>
      </template>

      <!-- Cột 6: Thao tác -->
      <template v-slot:item.actions="{ item }">
        <v-btn
          icon
          small
          color="indigo"
          :to="'/facility/assets?category=' + item.id"
          title="Xem danh sách thiết bị thuộc đầu mục"
        >
          <v-icon small>mdi-devices</v-icon>
        </v-btn>
        <v-btn icon small color="primary" @click="onEdit(item)" title="Sửa đầu mục">
          <v-icon small>mdi-pencil</v-icon>
        </v-btn>
        <v-btn icon small color="error" @click="onRemove(item)" title="Xóa đầu mục">
          <v-icon small>mdi-delete</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <!-- Dialog sửa đầu mục -->
    <CategoryEditDialog :state="editDialog" :categoryData="selectedItem" @updated="refresh()" />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { get } from 'lodash'
import CategoryEditDialog from '@/modules/facility/category/CategoryEditDialog.vue'

const headers = [
  { text: 'Tên đầu mục', value: 'name', align: 'left', sortable: false },
  { text: 'Mã đầu mục', value: 'code', align: 'left', sortable: false, width: '150px' },
  { text: 'Đơn vị tính', value: 'units', align: 'left', sortable: false },
  { text: 'Thẻ đính kèm', value: 'tags', align: 'left', sortable: false },
  { text: 'Ghi chú', value: 'description', align: 'left', sortable: false },
  { text: 'Thao tác', value: 'actions', align: 'left', sortable: false, width: '130px' }
]

export default {
  name: 'CategoryDataTable',
  components: {
    CategoryEditDialog
  },
  data() {
    return {
      headers,
      editDialog: false,
      selectedItem: null
    }
  },
  computed: {
    ...mapState('FacilityCategories', ['categories', 'totalItems', 'pageText', 'loading']),
    displayCategories() {
      if (!this.categories || !this.categories.length) return []

      const itemMap = new Map()
      const childrenMap = new Map()
      const rootItems = []

      // Index all items
      this.categories.forEach(item => {
        itemMap.set(item.id, item)
        childrenMap.set(item.id, [])
      })

      // Group children by parent
      this.categories.forEach(item => {
        const parentId = item.parent && (item.parent.id || item.parent)
        if (parentId && itemMap.has(parentId)) {
          childrenMap.get(parentId).push(item)
        } else {
          // If parent is not in the list (e.g. search filter), treat as standalone item
          rootItems.push(item)
        }
      })

      const result = []

      const traverse = (item, treeDepth = 1, isChildInTree = false) => {
        const children = childrenMap.get(item.id) || []

        // Preserve actual database level
        const displayLevel = item.level ? item.level : item.parent ? 2 : 1

        result.push({
          ...item,
          displayLevel,
          treeDepth,
          isChildInTree
        })

        // Recursively append children right under their parent
        children.forEach(child => {
          traverse(child, treeDepth + 1, true)
        })
      }

      rootItems.forEach(root => {
        traverse(root, 1, false)
      })

      return result
    }
  },
  async created() {
    await this.refresh()
  },
  methods: {
    get,
    ...mapActions('FacilityCategories', ['requestPageSettings', 'searchCategoryList', 'removeCategory']),
    async refresh(query) {
      this.searchCategoryList(query || {})
    },
    getTagColor(tag) {
      const title = String(tag || '').toLowerCase()
      return 'primary'
    },
    getItemUnits(item) {
      if (!item) return []
      if (Array.isArray(item.units) && item.units.length) {
        return item.units.filter(Boolean)
      }
      if (typeof item.units === 'string' && item.units.trim()) {
        return item.units.includes(',') ? item.units.split(',').map(u => u.trim()).filter(Boolean) : [item.units.trim()]
      }
      return []
    },
    getItemTags(item) {
      if (!item) return []
      if (Array.isArray(item.tags) && item.tags.length) {
        return item.tags.filter(Boolean)
      }
      if (typeof item.tags === 'string' && item.tags.trim()) {
        return item.tags.includes(',')
          ? item.tags
              .split(',')
              .map(t => t.trim())
              .filter(Boolean)
          : [item.tags.trim()]
      }
      if (item.academicLevel) {
        const alTitle =
          (item.academicLevel && (item.academicLevel.title || item.academicLevel.name)) || item.academicLevel
        if (alTitle) return [String(alTitle)]
      }
      return []
    },
    getTagFullName(tag) {
      if (!tag) return ''
      const title = String(tag).trim()
      if (title.toLowerCase() === 'cấp 1' || title.toLowerCase().includes('tiểu học')) return `${title} (Tiểu học)`
      if (title.toLowerCase() === 'cấp 2' || title.toLowerCase().includes('thcs'))
        return `${title} (Trung học cơ sở - THCS)`
      if (title.toLowerCase() === 'cấp 3' || title.toLowerCase().includes('thpt'))
        return `${title} (Trung học phổ thông - THPT)`
      return title
    },
    getItemClass(item) {
      if (item.isChildInTree) {
        return 'child-category-row'
      }
      return 'parent-category-row'
    },
    onEdit(item) {
      this.selectedItem = item
      this.editDialog = !this.editDialog
    },
    onRemove(item) {
      this.$dialog.confirm({
        title: 'Thông báo xác nhận',
        text: `Bạn có chắc muốn xóa đầu mục "${item.name}"? Bạn không thể hoàn tác hành động.`,
        okText: 'Xóa',
        cancelText: 'Hủy',
        done: async () => {
          this.$loading.active = true
          try {
            await this.removeCategory(item.id)
            this.$alert.deleteSuccess()
          } catch (error) {
            this.$alert.deleteError(error)
          } finally {
            this.$loading.active = false
          }
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.category-name-cell {
  min-height: 36px;
}

.tree-branch-line {
  display: inline-block;
  width: 8px;
  height: 10px;
  border-left: 1.5px solid #94a3b8;
  border-bottom: 1.5px solid #94a3b8;
  margin-right: 6px;
  margin-top: -5px;
  flex-shrink: 0;
  user-select: none;
}

::v-deep tr.child-category-row {
  background-color: #fafbfc;
  &:hover {
    background-color: #f1f5f9 !important;
  }
}

::v-deep tr.parent-category-row {
  background-color: #ffffff;
}
</style>
